/**
 * Harbor-edge timelines: intro, one timeline per letter section, sealing, prophecy.
 *
 * Section timelines are NON-skippable wrappers (letter beats → memory → beats → setFlag) so that a
 * cancel press only ever skips one nested thing: the memory stage owns its own skip, and the sealing /
 * prophecy cinematics are launched as nested skippable timelines through `fx: play-timeline`
 * (handled by PrologueEffects). Play and skip therefore converge on the same flag/mark state.
 *
 * Staging constraints (critic round 2): the harbor map is 640×384 under a 640×360 view, so the camera
 * can only scroll 0..24 px vertically and never horizontally; the dialogue box covers screen y ≥ 238
 * and the letter panel covers 110..530 × 20..228. The scene closes the letter panel before every
 * nested cinematic (PrologueScene.playNested) so the sealing / hand-over is seen, and 赵呈博 watches
 * from the pier's east end (feet at world y 220 → screen ≤ 200) so he is on screen for his monologue.
 */
import type { TimelineData, TimelineStep } from '@/core/types/contracts';
import { LETTER_SECTIONS, type LetterSection } from '../letter';
import { artState } from '../actors';

/** Effect id understood by PrologueEffects: plays a registered timeline on a nested skippable player. */
export const PLAY_TIMELINE_FX = 'play-timeline';

const letterSteps = (ids: readonly string[]): TimelineStep[] => ids.map((paragraph) => ({ op: 'letter', paragraph }));

/** The timeline id that plays a section (data-driven; the scene resolves by flag). */
export function sectionTimelineId(flag: string): string { return `prologue-section-${flag}`; }

function sectionTimeline(section: LetterSection): TimelineData {
  const steps: TimelineStep[] = [...letterSteps(section.before)];
  if (section.memory) steps.push({ op: 'launchMemory', stage: section.memory });
  steps.push(...letterSteps(section.after));
  if (section.cinematic) steps.push({ op: 'fx', effect: PLAY_TIMELINE_FX, params: { id: section.cinematic }, wait: true });
  steps.push({ op: 'setFlag', flag: section.flag });
  return { id: sectionTimelineId(section.flag), skippable: false, steps };
}

/**
 * Pier positions (world px; map tile (5,4) is the desk, (13,4) the gangway foot, (17,6) the pier's
 * east end where 赵呈博 watches). Screen y = world y − camera scroll (20 after the intro pan, ≤ 24).
 */
export const HARBOR_POS = {
  desk: { x: 176, y: 156 },
  gangway: { x: 432, y: 156 },
  handover: { x: 432, y: 188 },
  /** 赵呈博's watch position: the east end of the pier, fully above the dialogue box. */
  zhao: { x: 560, y: 220 },
  /** Where he steps to during the monologue (a little closer, still "远处"). */
  zhaoStep: { x: 520, y: 220 },
  /** Where the unloading sailors head off to at dusk (the sand below the pier, fully above the dialogue box). */
  sailorRest: [{ x: 256, y: 252 }, { x: 320, y: 252 }],
} as const;

/** Camera target that keeps the whole pier (desk, gangway, 赵) framed; the clamp lands on scrollY 20. */
const PIER_CAMERA = { x: HARBOR_POS.desk.x + 40, y: 200 } as const;

export const PROLOGUE_INTRO: TimelineData = {
  id: 'prologue-intro',
  skippable: true,
  steps: [
    { op: 'music', key: 'bgm-prologue-letter', fadeMs: 1200 },
    { op: 'camera', x: 448, y: 120, ms: 0 },
    { op: 'wait', ms: 500 },
    { op: 'parallel', steps: [
      { op: 'camera', x: PIER_CAMERA.x, y: PIER_CAMERA.y, ms: 3200 },
      { op: 'move', actor: 'jiang', x: HARBOR_POS.desk.x, y: HARBOR_POS.desk.y, speed: 64, wait: true },
    ] },
    { op: 'face', actor: 'jiang', facing: 'up' },
    { op: 'anim', actor: 'jiang', state: 'sit' },
    { op: 'wait', ms: 400 },
    { op: 'say', dialogue: 'prologue-arrival' },
    { op: 'anim', actor: 'jiang', state: 'write' },
  ],
};

/** 封蜡、装瓶、交给返航者 — played with the letter panel closed (the scene closes it first). */
export const PROLOGUE_LETTER_SENT: TimelineData = {
  id: 'prologue-letter-sent',
  skippable: true,
  steps: [
    { op: 'camera', x: PIER_CAMERA.x, y: PIER_CAMERA.y, ms: 0 },
    { op: 'anim', actor: 'jiang', state: 'idle' },
    { op: 'face', actor: 'jiang', facing: 'down' },
    { op: 'wait', ms: 300 },
    { op: 'fx', effect: 'seal-wax', params: { actor: 'jiang' }, wait: true },
    { op: 'sfx', key: 'sfx-seal-wax' },
    { op: 'say', dialogue: 'prologue-seal' },
    { op: 'sfx', key: 'sfx-bottle-cork' },
    { op: 'move', actor: 'jiang', x: HARBOR_POS.handover.x, y: HARBOR_POS.handover.y, speed: 72, wait: true },
    { op: 'face', actor: 'jiang', facing: 'right' },
    { op: 'face', actor: 'returner', facing: 'left' },
    { op: 'say', dialogue: 'prologue-handover' },
    { op: 'anim', actor: 'returner', state: artState('receive') },
    { op: 'fx', effect: 'hand-bottle', params: { from: 'jiang', to: 'returner' }, wait: true },
    { op: 'anim', actor: 'returner', state: 'idle' },
    { op: 'move', actor: 'returner', x: HARBOR_POS.gangway.x, y: HARBOR_POS.gangway.y, speed: 56, wait: true },
    { op: 'face', actor: 'returner', facing: 'up' },
    { op: 'fx', effect: 'actor-fade', params: { actor: 'returner', ms: 600 }, wait: true },
    { op: 'face', actor: 'jiang', facing: 'up' },
    { op: 'wait', ms: 500 },
  ],
};

/**
 * 视角切换到赵呈博的独白演出（内心文字 + 镜头）: dusk falls, the light narrows on 赵呈博 at the pier's
 * east end, he steps toward the edge and ponders while the narration runs; the sundial omen swings
 * over him during the prophecy sentences. Nothing is explained.
 */
export const PROLOGUE_PROPHECY: TimelineData = {
  id: 'prologue-prophecy',
  skippable: true,
  steps: [
    { op: 'music', key: 'bgm-prologue-prophecy', fadeMs: 2000 },
    { op: 'fx', effect: 'dusk-tint', params: { ms: 2500 }, wait: false },
    // the unloading is done for the day: the sailors leave the pier (and 赵's corner) before the light narrows
    { op: 'move', actor: 'sailor-1', x: HARBOR_POS.sailorRest[0].x, y: HARBOR_POS.sailorRest[0].y, speed: 60, wait: false },
    { op: 'move', actor: 'sailor-2', x: HARBOR_POS.sailorRest[1].x, y: HARBOR_POS.sailorRest[1].y, speed: 60, wait: false },
    { op: 'face', actor: 'zhao', facing: 'left' },
    { op: 'parallel', steps: [
      { op: 'camera', x: HARBOR_POS.zhao.x, y: HARBOR_POS.zhao.y, ms: 1400 },
      { op: 'fx', effect: 'focus-actor', params: { actor: 'zhao', ms: 1400 }, wait: true },
    ] },
    { op: 'move', actor: 'zhao', x: HARBOR_POS.zhaoStep.x, y: HARBOR_POS.zhaoStep.y, speed: 32, wait: true },
    { op: 'face', actor: 'zhao', facing: 'left' },
    { op: 'anim', actor: 'zhao', state: artState('ponder') },
    { op: 'emote', actor: 'zhao', emote: 'think', ms: 1200 },
    { op: 'say', dialogue: 'prologue-prophecy-1' },
    { op: 'fx', effect: 'sundial-omen', params: { actor: 'zhao' }, wait: false },
    { op: 'say', dialogue: 'prologue-prophecy-2' },
    { op: 'say', dialogue: 'prologue-prophecy-3' },
    { op: 'anim', actor: 'zhao', state: 'idle' },
    { op: 'wait', ms: 500 },
  ],
};

export const HARBOR_TIMELINES: TimelineData[] = [
  PROLOGUE_INTRO,
  ...LETTER_SECTIONS.map(sectionTimeline),
  PROLOGUE_LETTER_SENT,
  PROLOGUE_PROPHECY,
];
